import { useReducer, useCallback } from 'react'
import { computeFileSHA256 } from '@/shared/lib/crypto'
import { verifyPDFSignature } from '../services/signatureApi'
import type {
  SignatureVerificationState,
  SignatureVerificationAction,
} from '../types/signature.types'

const initialState: SignatureVerificationState = {
  file: null,
  localHash: null,
  isHashing: false,
  isVerifying: false,
  result: null,
  error: null,
}

function reducer(
  state: SignatureVerificationState,
  action: SignatureVerificationAction
): SignatureVerificationState {
  switch (action.type) {
    case 'SET_FILE':
      return { ...state, file: action.payload, error: null }
    case 'SET_LOCAL_HASH':
      return { ...state, localHash: action.payload }
    case 'SET_IS_HASHING':
      return { ...state, isHashing: action.payload }
    case 'SET_IS_VERIFYING':
      return { ...state, isVerifying: action.payload }
    case 'SET_RESULT':
      return { ...state, result: action.payload, error: null }
    case 'SET_ERROR':
      return { ...state, error: action.payload, result: null }
    case 'RESET':
      return initialState
    default:
      return state
  }
}

export function useSignatureVerification() {
  const [state, dispatch] = useReducer(reducer, initialState)

  /**
   * Handle file selection
   */
  const handleFileSelect = useCallback(async (file: File) => {
    dispatch({ type: 'SET_FILE', payload: file })
    dispatch({ type: 'SET_IS_HASHING', payload: true })

    try {
      // Compute hash locally (Privacy-by-Design)
      const hash = await computeFileSHA256(file)
      dispatch({ type: 'SET_LOCAL_HASH', payload: hash })
    } catch (error) {
      dispatch({
        type: 'SET_ERROR',
        payload: 'Error al calcular hash del archivo',
      })
    } finally {
      dispatch({ type: 'SET_IS_HASHING', payload: false })
    }
  }, [])

  /**
   * Verify signature
   */
  const verify = useCallback(async () => {
    if (!state.file || !state.localHash) {
      dispatch({
        type: 'SET_ERROR',
        payload: 'Debe seleccionar un archivo primero',
      })
      return
    }

    dispatch({ type: 'SET_IS_VERIFYING', payload: true })

    try {
      const result = await verifyPDFSignature(state.file, state.localHash)
      dispatch({ type: 'SET_RESULT', payload: result })
    } catch (error) {
      const message =
        error && typeof error === 'object' && 'detail' in error
          ? (error as { detail: string }).detail
          : 'Error al verificar la firma'
      dispatch({ type: 'SET_ERROR', payload: message })
    } finally {
      dispatch({ type: 'SET_IS_VERIFYING', payload: false })
    }
  }, [state.file, state.localHash])

  /**
   * Reset state
   */
  const reset = useCallback(() => {
    dispatch({ type: 'RESET' })
  }, [])

  return {
    ...state,
    handleFileSelect,
    verify,
    reset,
  }
}
